import { useState } from "react";
import { AlertTriangle, Loader2, Wrench } from "lucide-react";

import { usePlanchadoFlotaPlan, usePlancharFlota } from "@/hooks/useConfig";

/**
 * Botón de planchar toda la flota.
 *
 * **Se abre en seco.** Primero enseña qué cambiaría y en cuántos equipos; sólo
 * después aparece el botón que escribe. Escribir en cientos de equipos del
 * cliente no puede estar a un clic de distancia.
 *
 * El resumen muestra CUATRO desenlaces, no dos. `no leídos` es el que engaña:
 * un equipo perfecto y uno cuya foto no se pudo leer devuelven lo mismo —nada
 * fuera de golden—, y juntarlos hace que el botón diga "todo listo" habiendo
 * ignorado a media flota.
 */
export function PlancharFlota() {
  const [abierto, setAbierto] = useState(false);
  const plan = usePlanchadoFlotaPlan(abierto);
  const aplicar = usePlancharFlota();
  const r = aplicar.data ?? plan.data;

  if (!abierto) {
    return (
      <button
        type="button"
        onClick={() => setAbierto(true)}
        className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors hover:bg-muted"
      >
        <Wrench className="size-3.5" />
        Planchar la flota…
      </button>
    );
  }

  return (
    <div className="rounded-lg border p-3 text-sm">
      <div className="mb-2 flex items-center gap-2">
        <Wrench className="size-4" />
        <span className="font-medium">Planchar la flota</span>
        <button
          type="button"
          onClick={() => setAbierto(false)}
          className="ml-auto text-xs text-muted-foreground hover:underline"
        >
          cerrar
        </button>
      </div>

      {plan.isLoading && (
        <p className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> calculando qué haría…
        </p>
      )}

      {plan.isError && <p className="text-destructive">No se pudo calcular el plan.</p>}

      {r && (
        <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Dato n={r.resumen.a_planchar} t="a planchar" />
            <Dato n={r.resumen.ya_en_golden} t="ya en golden" />
            <Dato n={r.resumen.no_leidos} t="NO leídos" alerta />
            <Dato n={r.fallaron ?? 0} t="fallaron" />
          </div>

          {r.resumen.no_leidos > 0 && (
            <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-500">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
              <span>
                De {r.resumen.no_leidos} equipos <strong>no se sabe</strong> cómo están: su
                última lectura falló. No se van a tocar, y tampoco cuentan como conformes.
              </span>
            </p>
          )}

          {r.resumen.con_zona_horaria > 0 && (
            <p className="mt-1 text-xs text-muted-foreground">
              {r.resumen.con_zona_horaria} llevan zona horaria.{" "}
              <strong>HikCentral la repone</strong>: un planchado correcto puede deshacerse
              en las horas siguientes, así que el veredicto lo da la cosecha del día
              siguiente.
            </p>
          )}

          {r.resumen.foto_mas_vieja_h != null && r.resumen.foto_mas_vieja_h > 24 && (
            <p className="mt-1 text-xs text-amber-600 dark:text-amber-500">
              La foto más vieja del plan tiene {Math.round(r.resumen.foto_mas_vieja_h)} h. Se
              plancha contra esa lectura: conviene recosechar antes.
            </p>
          )}

          {Object.keys(r.sin_aplicador ?? {}).length > 0 && (
            <p className="mt-1 text-xs text-muted-foreground">
              Sin forma de escribirles todavía:{" "}
              {Object.entries(r.sin_aplicador)
                .map(([f, n]) => `${f} (${n})`)
                .join(", ")}
              .
            </p>
          )}

          {r.modo === "aplicado" ? (
            <div className="mt-3 rounded-md bg-muted p-2 text-xs">
              <p>
                <strong>{r.planchados ?? 0}</strong> planchados ·{" "}
                <strong>{r.fallaron ?? 0}</strong> fallaron
                {(r.bloqueados_por_candado ?? 0) > 0 && (
                  <>
                    {" · "}
                    <strong>{r.bloqueados_por_candado}</strong> no se tocaron porque su
                    familia tiene la escritura apagada
                  </>
                )}
              </p>
              {(r.zona_horaria_quedo ?? 0) + (r.zona_horaria_revirtio_ya ?? 0) > 0 && (
                <p className="mt-1">
                  Zona horaria: {r.zona_horaria_quedo ?? 0} quedaron,{" "}
                  {r.zona_horaria_revirtio_ya ?? 0} revirtieron ya.{" "}
                  <span className="text-muted-foreground">
                    Los que revierten horas después no se ven aquí.
                  </span>
                </p>
              )}
            </div>
          ) : (
            <button
              type="button"
              disabled={aplicar.isPending || r.resumen.a_planchar === 0}
              onClick={() => aplicar.mutate()}
              className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-amber-500 px-3 py-1.5 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-50 disabled:opacity-50 dark:text-amber-500 dark:hover:bg-amber-950"
            >
              {aplicar.isPending ? (
                <>
                  <Loader2 className="size-3.5 animate-spin" /> escribiendo…
                </>
              ) : (
                <>
                  <Wrench className="size-3.5" /> Aplicar a {r.resumen.a_planchar} equipos
                </>
              )}
            </button>
          )}

          {aplicar.isError && (
            <p className="mt-2 text-xs text-destructive">
              No se pudo aplicar. Si la escritura está apagada para todas las familias, el
              backend lo rechaza a propósito.
            </p>
          )}
        </>
      )}
    </div>
  );
}

function Dato({ n, t, alerta }: { n: number; t: string; alerta?: boolean }) {
  return (
    <div className="rounded-md border p-2">
      <div
        className={`text-lg font-semibold tabular-nums ${
          alerta && n > 0 ? "text-amber-600 dark:text-amber-500" : ""
        }`}
      >
        {n}
      </div>
      <div className="text-xs text-muted-foreground">{t}</div>
    </div>
  );
}
